// The coach's public-address voice (docs/plans/2026-10-18-ride-coach.md, data/story/s1e1.announce.json). Plays the
// recorded clips from public/audio/<id>.ogg, ids as listed in src/client/voice.json.
//
// ONE channel, on purpose. A real announcement is ~11 s (Hindi then English, a slow voice) and a metro ride here is
// 30-45 s for ten stations, so the stretch between two stations is about 4 s: announcing every station would stack
// a dozen voices on top of each other. A line that arrives while another is speaking is dropped, as a PA system
// does not cut itself off; a line of higher priority does cut in. The destination is priority 2, so the one that
// matters is always heard. A missing file is silence, never an error, the same rule as audio.ts.
//
// A line the scene ends on (the ride handing over to the arrival) is not chopped mid-word: the clip turns round and
// plays back over what it just said, fading out over TAIL_S, then ends (fadeOut). That needs WebAudio, so the clips
// play through the game's own context (audio.ts) once it is unlocked, and through an <audio> element before that.

export type Clip = {
  play(): Promise<void> | void; pause(): void; onend: (() => void) | null;
  /** end without a cut: back over the last TAIL_S, reversed and fading to silence (falls back to pause) */
  fadeOut?(): void;
};
/** `volume` 0..1: the platform's far-off PA plays under 1 */
export type MakeClip = (src: string, volume?: number) => Clip;
/** How long a line takes to wind down when the scene ends on it, s. */
export const TAIL_S = 1.2;

/** 'Chandni Chowk' -> 'pa-next-chandni-chowk': the id data/story/s1e1.announce.json gives the approach line. */
export const approachId = (station: string) =>
  'pa-next-' + station.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const html5: MakeClip = (src, volume = 1) => {
  const a = new Audio(src); a.volume = Math.max(0, Math.min(1, volume));
  const c: Clip = { play: () => a.play(), pause: () => a.pause(), onend: null };
  a.onended = a.onerror = () => c.onend?.();
  return c;
};

/** The channel's samples back to front (the tail plays the clip in reverse). */
export function reversed(ctx: BaseAudioContext, b: AudioBuffer): AudioBuffer {
  const r = ctx.createBuffer(b.numberOfChannels, b.length, b.sampleRate);
  for (let c = 0; c < b.numberOfChannels; c++) { const d = r.getChannelData(c); d.set(b.getChannelData(c)); d.reverse(); }
  return r;
}

/** Where the reversed tail starts and how long it runs, for a cut `pos` s into a clip of `dur` s. */
export function tailOf(pos: number, dur: number): { offset: number; len: number } {
  const p = Math.max(0, Math.min(dur, pos));
  return { offset: dur - p, len: Math.min(TAIL_S, p) };
}

/** Clips through WebAudio, so a line can wind down in reverse; `out` gives the context and node, null until unlocked. */
export function webAudio(out: () => { ctx: AudioContext; node: AudioNode } | null): MakeClip {
  const bufs = new Map<string, Promise<{ fwd: AudioBuffer; rev: AudioBuffer }>>();
  return (src, volume = 1) => {
    const o = out();
    if (!o) return html5(src, volume);
    const { ctx } = o, node = ctx.createGain(); // the line's own level, into the game's announcement node
    node.gain.value = Math.max(0, Math.min(1, volume)); node.connect(o.node);
    let cur: { s: AudioBufferSourceNode; t0: number; b: { fwd: AudioBuffer; rev: AudioBuffer } } | null = null, gone = false;
    const c: Clip = {
      onend: null,
      play() {
        let p = bufs.get(src);
        if (!p) {
          p = fetch(src).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${src}: ${r.status}`))))
            .then((a) => ctx.decodeAudioData(a)).then((fwd) => ({ fwd, rev: reversed(ctx, fwd) }));
          p.catch(() => bufs.delete(src)); // a missing file is tried again next time
          bufs.set(src, p);
        }
        return p.then((b) => {
          if (gone) return;
          const s = ctx.createBufferSource(); s.buffer = b.fwd; s.connect(node);
          s.onended = () => { if (cur?.s === s) { cur = null; c.onend?.(); } };
          cur = { s, t0: ctx.currentTime, b }; s.start();
        });
      },
      pause() { gone = true; const k = cur; cur = null; if (k) { k.s.onended = null; k.s.stop(); } },
      fadeOut() {
        const k = cur; c.pause(); if (!k) return;
        const { offset, len } = tailOf(ctx.currentTime - k.t0, k.b.fwd.duration);
        if (len <= 0.02) return;
        const s = ctx.createBufferSource(), g = ctx.createGain(), t = ctx.currentTime;
        s.buffer = k.b.rev; s.connect(g).connect(node);
        g.gain.setValueAtTime(1, t); g.gain.linearRampToValueAtTime(0, t + len);
        s.start(t, offset, len);
      },
    };
    return c;
  };
}

export class Announcer {
  private cur: { clip: Clip; prio: number; id: string } | null = null;
  private seen = new Set<string>();
  constructor(private muted: () => boolean = () => false, private make: MakeClip = html5) {}

  get busy() { return this.cur !== null; }
  /** Whether this clip is the one playing: a loop stops only its own line, never another's. */
  playing(id: string) { return this.cur?.id === id; }

  /** Say a clip. Returns whether it was started. `once` keys a line to one telling per ride; `volume` is 0..1. */
  say(id: string, prio = 1, once = true, volume = 1): boolean {
    if (this.muted()) return false;
    if (once && this.seen.has(id)) return false;
    if (this.cur) { if (prio <= this.cur.prio) return false; this.stop(); }
    if (once) this.seen.add(id);
    const clip = this.make(`/audio/${id}.ogg`, volume), me = { clip, prio, id };
    this.cur = me;
    clip.onend = () => { if (this.cur === me) this.cur = null; };
    try { Promise.resolve(clip.play()).catch(() => { if (this.cur === me) this.cur = null; }); }
    catch { this.cur = null; }
    return true;
  }

  /** Stop the line now (a higher priority cutting in), or with `soft`, wind it down in reverse (the scene ending on it). */
  stop(soft = false) {
    const c = this.cur; this.cur = null; if (!c) return;
    c.clip.onend = null;
    if (soft && c.clip.fadeOut) c.clip.fadeOut(); else c.clip.pause();
  }
  /** A new ride: every line may be told again. */
  reset() { this.stop(true); this.seen.clear(); }
}
